import './DesignerPalette.css';

import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import NodeContent from './NodeContent';
import { useSelector } from 'react-redux';
import { useToasts } from '../../contexts/toasts';
import { memo } from 'react';
import AppSearch from '../common/AppSearch';
import { Alert, Badge, Col, Flex, Pagination, Tabs, TabsProps, Tooltip } from 'antd';
import { useCallback } from 'react';
import { getNodePortsFromProperties, serviceHasProperty } from '../../utils';
import { AccessLevel, PagedResults } from '../../models/generics';
import { Services } from '../../api/Services';
import { BdaService } from '../../models/BdaService';
import { Datasets } from '../../api/Datasets';
import { BdaDataset } from '../../models/BdaDataset';
import { Models } from '../../api/Models';
import { BdaModel } from '../../models/BdaModel';
import PaletteFiltersModal, { PaletteFilters } from './PaletteFiltersModal';
import { debounce } from 'lodash';
import IconButton from '../common/IconButton';
import { PaletteNode } from '../../models/Designer';
import { Link } from 'react-router-dom';
import { sectionLabelsSelector } from '../../slices/sectionsSlice';
import { RootState } from '../../store';
import AppLoader from '../common/AppLoader';

const EmptyAlert = memo(({ group }: { group: 'services' | 'datasets' | 'models' }) => (
  <Alert
    type="info"
    message={`No ${group} found`}
    showIcon
  />
))

type AssetType = "services" | "models" | "datasets";

type Props = {
  onShowServiceMarkdown?: (serviceId: number) => void;
  applicationAccessLevel?: AccessLevel;
}

function DesignerPalette({ onShowServiceMarkdown, applicationAccessLevel }: Props) {
  console.log("[Palette] re-render");
  const [totalServices, setTotalServices] = useState<number>();
  const [totalDatasets, setTotalDatasets] = useState<number>();
  const [totalModels, setTotalModels] = useState<number>();
  const [services, setServices] = useState<PaletteNode[]>();
  const [datasets, setDatasets] = useState<PaletteNode[]>();
  const [models, setModels] = useState<PaletteNode[]>();

  const [servicesError, setServicesError] = useState(false);
  const [datasetsError, setDatasetsError] = useState(false);
  const [modelsError, setModelsError] = useState(false);

  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [selectedTab, setSelectedTab] = useState<AssetType>("services");
  const [servicesFilters, setServicesFilters] = useState<PaletteFilters>({ page: 1 });
  const [serviceSearch, setServiceSearch] = useState("");
  const [datasetsFilters, setDatasetsFilters] = useState<PaletteFilters>({ page: 1 });
  const [datasetSearch, setDatasetSearch] = useState("");
  const [modelsFilters, setModelsFilters] = useState<PaletteFilters>({ page: 1 });
  const [modelSearch, setModelSearch] = useState("");

  const serviceLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "services"));
  const datasetLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "datasets"));
  const modelLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "models"));

  const { showToast } = useToasts();

  const tabs: TabsProps['items'] = useMemo(() => ([
    { key: 'services', label: serviceLabels.plural || "Services", icon: <FontAwesomeIcon icon={solid('cubes')} />, },
    { key: 'datasets', label: datasetLabels.plural || "Datasets", icon: <FontAwesomeIcon icon={solid('database')} />, },
    { key: 'models', label: modelLabels.plural || "Models", icon: <FontAwesomeIcon icon={solid('graduation-cap')} />, },
  ]), [serviceLabels, datasetLabels, modelLabels]);

  const assetLabels: Record<AssetType, string> = useMemo(() => ({
    services: serviceLabels.plural || "Services",
    datasets: datasetLabels.plural || "Datasets",
    models: modelLabels.plural || "Models",
  }), [serviceLabels, datasetLabels, modelLabels]);

  const data: PaletteNode[] | undefined = useMemo(() => {
    return selectedTab === 'services'
      ? services
      : selectedTab === 'datasets'
        ? datasets
        : models;
  }, [datasets, models, services, selectedTab]);

  const totalElements = useMemo(() => {
    if (selectedTab === "services") return totalServices;
    else if (selectedTab === "datasets") return totalDatasets;
    else return totalModels;
  }, [selectedTab, totalServices, totalDatasets, totalModels]);

  const currentPage = useMemo(() => {
    if (selectedTab === "services") return servicesFilters.page;
    else if (selectedTab === "datasets") return datasetsFilters.page;
    else return modelsFilters.page;
  }, [selectedTab, servicesFilters, datasetsFilters, modelsFilters]);

  const accessLevelFilter = useMemo(() => {
    return applicationAccessLevel === AccessLevel.PRIVATE
      ? null
      : applicationAccessLevel === AccessLevel.TEAM
        ? "PUBLIC,TEAM"
        : applicationAccessLevel;
  }, [applicationAccessLevel]);

  const activeFilters = useMemo(() => {
    const targetFilters = selectedTab === 'services'
      ? servicesFilters
      : selectedTab === 'datasets'
        ? datasetsFilters
        : modelsFilters;
    return Object.entries(targetFilters).filter(([key, value]) => key !== 'page' && key !== 'search' && value !== undefined && value.length).length;
  }, [selectedTab, servicesFilters, datasetsFilters, modelsFilters]);

  const openFiltersModal = useCallback(() => setShowFiltersModal(true), []);
  const handleFiltersModalClose = useCallback(() => setShowFiltersModal(false), []);
  const handleTabChange = useCallback((tab: string | null) => setSelectedTab(tab as AssetType), []);

  const handleMarkdownClick = useCallback((serviceId: number) => (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.stopPropagation();
    onShowServiceMarkdown?.(serviceId);
  }, [onShowServiceMarkdown]);

  const getServices = useCallback(() => {
    const params = new URLSearchParams([
      //["accessLevel", "PUBLIC"],
      ["page", `${currentPage || 1}`],
      ["size", "20"],
      ["sort", "id"],
      ["desc", "false"],
      ["designerMode", "true"],
    ]);
    //if (accessLevelFilter) params.set("accessLevel", accessLevelFilter);
    if (servicesFilters) {
      if (servicesFilters.tags && servicesFilters.tags.length > 0) params.set("tags", servicesFilters.tags.join(","));
      if (servicesFilters.search) params.set("freeText", servicesFilters.search);
      if (servicesFilters.categories) params.set("categories", servicesFilters.categories);
    }
    const llmModelKey = window.__env.DESIGNER__AGENT_LLM_MODEL_PROPERTY, promptKey = window.__env.DESIGNER__AGENT_PROMPT_PROPERTY;

    // TODO handle category id from CM instead of plaintext category name?
    Services.getServices(params)
      .then(
        ({ data: servicesPages }) => {
          const nodes = ((servicesPages as PagedResults<BdaService>).content).map<PaletteNode>(service => {
            const { inputs, outputs } = getNodePortsFromProperties(service.properties!);
            // TODO improve this (use category id instead of name?)
            const type = service.category?.name === 'Agent' ? 'agent' : service.category?.name === 'MCP' ? 'mcp' : 'service';

            return {
              id: service.id,
              name: service.name,
              type,
              tooltipContent: (
                <Flex align="center">
                  <Col className="text-center pe-2">
                    <Link to={`/services/${service.id}`} target="_blank" rel="noopener noreferrer">
                      <h6 className="mb-0">{service.name} {service.version}</h6>
                    </Link>
                    <p className="m-0">
                      <FontAwesomeIcon icon={solid('user')} />
                      <span className="ms-2">{service.owner?.name}</span>
                    </p>
                  </Col>
                  <IconButton className="p-0 ms-1" onClick={handleMarkdownClick(service.id!)}>
                    <FontAwesomeIcon color="white" size="xl" icon={solid('info-circle')} />
                  </IconButton>
                </Flex>
              ),
              meta: {
                label: service.label || service.name,
                version: service.version,
                owner: service.owner?.name,
                inputs,
                outputs,
                agentLlmModelConfig: serviceHasProperty(service, llmModelKey),
                agentPrompt: serviceHasProperty(service, promptKey),
              }
            }
          });
          setServices(nodes);
          setTotalServices((servicesPages as PagedResults<BdaService>).pageMetadata.totalElements);
        },
        () => {
          showToast('services-error', `Could not get ${serviceLabels.plural}`, `${serviceLabels.plural} Error`, 'danger');
          setServices([]);
          setServicesError(true);
        }
      );
  }, [currentPage, servicesFilters, showToast, handleMarkdownClick, serviceLabels]);

  const getDatasets = useCallback(() => {
    const params = new URLSearchParams([
      ["page", `${currentPage || 1}`],
      ["size", "20"],
      ["sort", "id"],
      ["desc", "false"],
      ['evaluation', "false"],
      ['created', "true"],
    ]);
    if (accessLevelFilter) params.set("accessLevel", accessLevelFilter);
    if (datasetsFilters?.search) params.set("freeText", datasetsFilters?.search);

    Datasets.getDatasets(params)
      .then(
        ({ data: datasetPages }) => {
          const nodes = ((datasetPages as PagedResults<BdaDataset>).content).map<PaletteNode>(dataset => {
            return {
              id: dataset.id,
              name: dataset.name,
              type: 'dataset',
              tooltipContent: <Link to={`/datasets/${dataset.id}`} target="_blank" rel="noopener noreferrer">{dataset.name}</Link>,
              meta: {
                datasetType: dataset.type,
                trailing: dataset.datasetFileType?.type || dataset.type,
              }
            }
          });
          setDatasets(nodes);
          setTotalDatasets((datasetPages as PagedResults<BdaDataset>).pageMetadata.totalElements);
        },
        () => {
          showToast('datasets-error', `Could not get ${datasetLabels.plural}`, `${datasetLabels.plural} Error`, 'danger');
          setDatasets([]);
          setDatasetsError(true);
        }
      );
  }, [datasetsFilters, currentPage, accessLevelFilter, showToast, datasetLabels]);

  const getModels = useCallback(() => {
    const params = new URLSearchParams();
    params.set("page", `${currentPage || 1}`);
    params.set("size", "20");
    params.set("sort", "id");
    params.set("desc", "false");
    params.set('evaluation', "false");
    params.set('created', "true");
    if (accessLevelFilter) params.set("accessLevel", accessLevelFilter);
    if (modelsFilters?.search) params.set("freeText", modelsFilters.search);

    Models.getModels(params)
      .then(
        ({ data: modelsPages }) => {
          const nodes = ((modelsPages as PagedResults<BdaModel>).content).map<PaletteNode>(model => ({
            id: model.id,
            name: model.name,
            type: 'model',
            tooltipContent: <Link to={`/models/${model.id}`} target="_blank" rel="noopener noreferrer">{model.name}</Link>,
            meta: {
              location: model.location,
              trailing: model.createDateTime,
            }
          }));
          setModels(nodes);
          setTotalModels((modelsPages as PagedResults<BdaModel>).pageMetadata.totalElements);
        },
        () => {
          showToast('models-error', `Could not get ${modelLabels.plural}`, `${modelLabels.plural} Error`, 'danger');
          setModels([]);
          setModelsError(true);
        }
      );
  }, [modelsFilters, currentPage, accessLevelFilter, showToast, modelLabels]);

  const handleDragStart = useCallback((node: PaletteNode, group: string) => (event: React.DragEvent<HTMLLIElement>) => {
    event.dataTransfer.setData('application/node-name', node.name);
    event.dataTransfer.setData('application/node-id', `${node.id}`);
    event.dataTransfer.setData('application/node-inputs', JSON.stringify(node.meta.inputs));
    event.dataTransfer.setData('application/node-outputs', JSON.stringify(node.meta.outputs));

    if (group === 'service' || group === 'agent' || group === 'mcp') {
      event.dataTransfer.setData('application/node-label', node.meta.label);
      event.dataTransfer.setData('application/node-category', node.type);
      event.dataTransfer.setData('application/node-version', node.meta.version);
      event.dataTransfer.setData('application/node-owner', node.meta.owner);
      if (node.meta.agentLlmModelConfig) event.dataTransfer.setData('application/agent-llm-model-config', "true");
      if (node.meta.agentPrompt) event.dataTransfer.setData('application/agent-prompt', "true");
    }
    else event.dataTransfer.setData('application/node-category', group);

    if (group === "model") {
      event.dataTransfer.setData('application/node-model-location', node.meta.location);
    }
    if (group === "dataset" || group === "model") {
      event.dataTransfer.setData('application/node-trailing', node.meta.trailing);
    }
    event.dataTransfer.effectAllowed = 'move';
  }, []);

  const reset = useCallback(() => {
    setServicesError(false);
    setDatasetsError(false);
    setModelsError(false);

    if (selectedTab === 'services' || !data) {
      setServices(undefined);
      getServices();
    }
    if (selectedTab === 'datasets' || !data) {
      setDatasets(undefined);
      getDatasets();
    }
    if (selectedTab === 'models' || !data) {
      setModels(undefined);
      getModels();
    }
  }, [selectedTab, data, getServices, getDatasets, getModels]);

  // eslint-disable-next-line
  useEffect(reset, [servicesFilters, datasetsFilters, modelsFilters]);

  const handleSearchChange = useCallback((search: string) => {
    if (selectedTab === "services") setServicesFilters(curr => ({ ...curr, page: 1, search }));
    else if (selectedTab === "datasets") setDatasetsFilters(curr => ({ ...curr, page: 1, search }));
    else setModelsFilters(curr => ({ ...curr, page: 1, search }));
  }, [selectedTab]);

  const debounceSearchChange = useMemo(() => debounce(handleSearchChange, 500), [handleSearchChange]);

  const handleInputValueChange = useCallback((search: string) => {
    if (selectedTab === "models") setModelSearch(search);
    if (selectedTab === "services") setServiceSearch(search);
    if (selectedTab === "datasets") setDatasetSearch(search);
    debounceSearchChange(search);
  }, [selectedTab, debounceSearchChange]);

  const handlePaletteFiltersSubmit = useCallback((filters: PaletteFilters) => {
    setServicesFilters(curr => ({ ...curr, ...filters }));
    handleFiltersModalClose();
  }, [handleFiltersModalClose]);

  const handlePageChange = useCallback((page: number, _pageSize: number) => {
    if (selectedTab === "services") setServicesFilters(curr => ({ ...curr, page }));
    else if (selectedTab === "datasets") setDatasetsFilters(curr => ({ ...curr, page }));
    else setModelsFilters(curr => ({ ...curr, page }));
  }, [selectedTab]);

  const renderPaletteNodes = useCallback(() => {
    if (data!.length === 0) return <EmptyAlert group={selectedTab} />

    return data!.map(node => {
      return (
        <Tooltip key={node.id} title={node.tooltipContent} mouseEnterDelay={0.3} placement="right">
          <li
            id={`${node.type}-${node.id}`}
            className={classNames(['designer-node', `${node.type}-node`])}
            onDragStart={handleDragStart(node, node.type)}
            draggable
          >
            <NodeContent
              title={node.name}
              type={node.type}
              version={node.meta.version}
              trailing={node.meta.trailing}
            />
          </li>
        </Tooltip>
      )
    });
  }, [data, selectedTab, handleDragStart]);

  const renderAsyncContent = useCallback(() => {
    if (
      (servicesError && selectedTab === "services") ||
      (datasetsError && selectedTab === "datasets") ||
      (modelsError && selectedTab === "models")
    ) return (
      <Alert
        showIcon
        type="error"
        message={`An error occurred while retrieving ${assetLabels[selectedTab]}, try again later.`}
        className="m-2"
      />
    );

    if (!data) return <AppLoader className="py-5" justify="center" size={100} />

    return (
      <div className="palette-nodes">
        {renderPaletteNodes()}
      </div>
    );
  }, [selectedTab, data, datasetsError, modelsError, servicesError, renderPaletteNodes, assetLabels]);

  const inputValue = useMemo(() => {
    if (selectedTab === "datasets") return datasetSearch;
    if (selectedTab === "models") return modelSearch;
    return serviceSearch;
  }, [selectedTab, datasetSearch, modelSearch, serviceSearch]);

  return (
    <>
      <aside className="designer-palette">
        <Tabs
          centered
          size="small"
          activeKey={selectedTab}
          onChange={handleTabChange}
          items={
            tabs!.map(tab => ({
              key: tab.key,
              icon: tab.icon,
              label: tab.label,
              tabKey: tab.key,
              children: (
                <>
                  <div className="p-2 border-bottom">
                    <Flex className="mb-2" align="center">
                      <AppSearch
                        placeholder={`Search ${assetLabels[selectedTab]}...`}
                        value={inputValue}
                        onChange={handleInputValueChange}
                        suffix={selectedTab === "services" && (
                          <Badge count={activeFilters}>
                            <IconButton className="p-1" tooltip={{ title: "Filters", placement: "right" }} disabled={!data} onClick={openFiltersModal}>
                              <FontAwesomeIcon icon={solid("filter")} />
                            </IconButton>
                          </Badge>
                        )}
                      />
                    </Flex>

                    <Flex align="center">
                      <Pagination
                        simple
                        // size="small"
                        showSizeChanger={false}
                        disabled={!data}
                        total={totalElements}
                        current={currentPage}
                        onChange={handlePageChange}
                        pageSize={20}
                      />

                      <small className="ms-auto text-secondary">({totalElements} found)</small>
                    </Flex>
                  </div>
                  {renderAsyncContent()}
                </>
              )
            }))
          }
        />

        <PaletteFiltersModal
          show={showFiltersModal}
          onClose={handleFiltersModalClose}
          filters={servicesFilters}
          onSubmit={handlePaletteFiltersSubmit}
        />
      </aside>
    </>
  );
}

export default memo(DesignerPalette);